/* eslint-disable class-methods-use-this */
/* eslint-disable max-classes-per-file */
/* eslint-disable camelcase */
import moment from 'moment/moment';
import type { Response } from '../services/__fixtures__/service-fixtures';
import type { GetGroupsParams } from '../services/groups.service';
import {
  useGetGroupsMembersQuery,
  useGetGroupsAppsQuery,
  useGetGroupsProjectsQuery,
  useGetGroupsLayersQuery,
} from '../services/groups.service';
import { MenuListItemProps } from '../components/menus/menu-list';
import { OptionItem } from '../components/cards/card.types';

export enum Resource {
  Members = 'members',
  Apps = 'apps',
  Layers = 'layers',
  Projects = 'projects',
}

export enum ResponseAttr {
  'members' = 'memberships',
  'apps' = 'forms',
  'layers' = 'layers',
  'projects' = 'projects',
}

type Association = 'formIds' | 'layerIds' | 'projectIds';

export enum ChangePermission {
  members = 'group_members',
  apps = 'group_forms',
  layers = 'group_layers',
  projects = 'group_projects',
}

type GroupUser = {
  email: string;
  firstName: string;
  lastName: string;
  resourceId: string;
}

export interface IGroup {
  id: string;
  name: string;
  isManaged: boolean;
  createdAt?: string;
  createdBy?: GroupUser;
  description?: string;
  formIds?: string[];
  layerIds?: string[];
  memberIds?: string[];
  projectIds?: string[];
  errors?: string[];
  data?: {
    errors: string[];
  }
  options?: OptionItem[];
}

export interface GroupsInheritedPermissions {
  inheritedForms: { [key: string]: string[] };
  inheritedLayers: { [key: string]: string[] };
  inheritedProjects: { [key: string]: string[] };
  inheritedFormIds: string[];
  inheritedLayerIds: string[];
  inheritedProjectIds: string[];
}

// GROUPS RESPONSES
export interface GroupsMembership {
  id: string;
  email: string;
  user: string;
  description: string;
  role_name?: string;
  avatar?: string;
  name?: string;
}

export interface GroupsForm {
  id: string;
  image: string;
  name?: string;
  description?: string;
  updated_at?: string;
  record_count?: number;
  record_changed_at?: string;
}

export interface GroupsLayer {
  id: string;
  name: string;
  description: string;
  image: string,
  type?: string;
  file_size?: number;
  layer_type?: string;
}

export interface GroupsProject {
  id: string;
  name: string;
  image: string,
  form_count?: string;
  record_count?: string;
}

export type GroupResource = GroupsMembership | GroupsForm | GroupsLayer | GroupsProject;
type Source = 'members' | 'forms' | 'layers' | 'projects' | 'groups';
type ResourceValues<T extends Source> = T extends 'members'
  ? { memberships: GroupsMembership[] }
  : T extends 'forms'
    ? { forms: GroupsForm[] }
    : T extends 'layers'
      ? { layers: GroupsLayer[] }
      : T extends 'projects'
        ? { projects: GroupsProject[] }
        : T extends 'groups'
          ? { groups: IGroup[] }
          : never;

export type GroupsResponse<T extends Source> = Partial<Response> & ResourceValues<T>;

interface GetSelectedGroupsPermissionsParams {
  allGroups?: IGroup[];
  selectedGroups?: string[];
}

interface GroupPermissionsReducer {
  acc: object;
  group: IGroup;
  resourcesField: Association;
}

export const transformGroup = (group: any): IGroup => {
  const {
    is_managed,
    form_ids,
    layer_ids,
    member_ids,
    project_ids,
    ...rest
  } = group;
  return {
    ...rest,
    formIds: form_ids,
    layerIds: layer_ids,
    memberIds: member_ids,
    projectIds: project_ids,
    isManaged: is_managed,
  };
};

export const messagesGroup = {
  createGroup: {
    success: 'Group successfully created.',
    error: 'There was an error creating the group.',
    pending: 'Adding group',
  },
  updateGroup: {
    success: 'Group successfully updated',
    error: 'There was a problem updating the group',
    pending: 'Loading update',
  },
  deleteGroup: {
    success: 'Group successfully deleted',
    error: 'There was a problem deleting the group',
    pending: 'Removing group',
  },
};

export const requiredErrorMessages = {
  nameRequired: 'A group name is required',
  nameAlreadyTaken: 'This group name already exists',
};

export const messagesPermissions = (item?: string, resource?: Resource, count?: number) => ({
  delete: {
    success: `${item} was removed from the group.`,
    error: `There was an error removing ${item} from the group.`,
    pending: `Removing ${item}`,
  },
  create: {
    one: {
      success: `${item} was added to the group.`,
      error: `There was an error adding ${item} to the group.`,
      pending: `Adding ${item}`,
    },
    many: {
      success: `${count} ${resource} were added to the group.`,
      error: `There was an error adding ${count} ${resource} to the group.`,
      pending: `Adding ${count} ${resource}`,
    },
  },
});

export function getResourceBehavior(resource: Resource): ResourceBehavior {
  switch (resource) {
    case Resource.Members:
      return new MembersBehavior();
    case Resource.Apps:
      return new AppsBehavior();
    case Resource.Layers:
      return new LayersBehavior();
    case Resource.Projects:
      return new ProjectsBehavior();
    default:
      throw new Error('Invalid resource');
  }
}

type ResourceText = 'App' | 'Map Layer' | 'Project' | 'Member';

/**
 * An abstract class that defines the behavior of a resource in a group.
 */
export abstract class ResourceBehavior {
  /**
   * The type of resource being managed.
   */
  abstract RESOURCE: Resource;

  /**
   * The display name of the resource being managed.
   */
  abstract RESOURCE_TEXT: ResourceText;

  /**
   * Returns a query object for fetching the resource.
   * @param params - The parameters for the query.
   * @param options - The options for the query.
   * @returns The query object.
   */
  abstract useQuery(params: GetGroupsParams, options?: any): any;

  /**
   * Maps a record object to a `MenuListItemProps` object.
   * @param item - The record object to map.
   * @returns The `MenuListItemProps` object.
   */
  abstract mapResourceToListItem(item: Record<string, any>): MenuListItemProps;

  /**
   * Returns a string that describes the consequences of deleting a resource.
   * @returns The delete text.
   */
  getDeleteText(): string {
    return `Members will lose access unless they are in another Group containing this ${this.RESOURCE_TEXT}.`;
  }

  /**
   * Returns a string that represents the placeholder text for a search input field.
   * @returns The placeholder text.
   */
  getPlaceholderText(): string {
    return 'Search name or description';
  }

  /**
   * Returns a string that describes an error message that may occur when fetching the resource.
   * @param isError - Whether an error occurred.
   * @returns The error message.
   */
  getErrorMessage(isError?: boolean): string {
    if (!isError) return '';
    return `There was an error fetching the ${this.RESOURCE}`;
  }
}

export class MembersBehavior extends ResourceBehavior {
  RESOURCE = Resource.Members;
  RESOURCE_TEXT = 'Member' as const;

  useQuery(params: GetGroupsParams, options?: any): any {
    return useGetGroupsMembersQuery(params, options);
  }

  mapResourceToListItem(item: Record<string, any>): MenuListItemProps {
    return {
      id: item.id,
      primaryText: item.user,
      secondaryText: item.email,
      caption: item.role_name || '',
    };
  }

  getDeleteText(): string {
    return 'Removing Members from a Group also removes their access to its Apps, Map Layers, and Projects.';
  }

  getPlaceholderText(): string {
    return 'Search name, email, or role';
  }
}

export class AppsBehavior extends ResourceBehavior {
  RESOURCE = Resource.Apps;
  RESOURCE_TEXT = 'App' as const;

  useQuery(params: GetGroupsParams, options?: any): any {
    return useGetGroupsAppsQuery(params, options);
  }

  mapResourceToListItem(item: Record<string, any>): MenuListItemProps {
    return {
      id: item.id,
      primaryText: item.name || '',
      secondaryText: `Last activity ${moment(item.updated_at).fromNow()}`,
      caption: `${item.record_count} ${item.record_count === 1 ? 'record' : 'records'}`,
    };
  }
}

export class LayersBehavior extends ResourceBehavior {
  RESOURCE = Resource.Layers;
  RESOURCE_TEXT = 'Map Layer' as const;

  useQuery(params: GetGroupsParams, options?: any): any {
    return useGetGroupsLayersQuery(params, options);
  }

  mapResourceToListItem(item: Record<string, any>): MenuListItemProps {
    return {
      id: item.id,
      primaryText: item.name,
      secondaryText: item.description,
      caption: item.type || '',
    };
  }
}

export class ProjectsBehavior extends ResourceBehavior {
  RESOURCE = Resource.Projects;
  RESOURCE_TEXT = 'Project' as const;

  useQuery(params: GetGroupsParams, options?: any): any {
    return useGetGroupsProjectsQuery(params, options);
  }

  mapResourceToListItem(item: Record<string, any>): MenuListItemProps {
    return {
      id: item.id,
      primaryText: item.name,
      secondaryText: `${item.form_count} apps using this project`,
      caption: `${item.record_count} records`,
    };
  }
}

const Group = () => (() => {
  const reducePermissionsWithGroupName = ({ ...permissions }: GroupPermissionsReducer) => {
    const { acc, group, resourcesField } = permissions;
    group[resourcesField]?.forEach((resourceId) => {
      if (!acc[resourceId]) {
        acc[resourceId] = [];
      }
      acc[resourceId].push(group.name);
    });
    return acc;
  };

  function reduceGroups(groups: IGroup[], selected: string[]) {
    return groups.filter((group) => selected.includes(group.id));
  }

  return {
    getSelectedGroupsPermissions({
      allGroups = [],
      selectedGroups = [],
    }: GetSelectedGroupsPermissionsParams = {}): GroupsInheritedPermissions {
      const selectedGroupPermissions = reduceGroups(allGroups, selectedGroups);

      const inheritedForms = selectedGroupPermissions.reduce(
        (acc, group) => reducePermissionsWithGroupName({ acc, group, resourcesField: 'formIds' }),
        {},
      );
      const inheritedLayers = selectedGroupPermissions.reduce(
        (acc, group) => reducePermissionsWithGroupName({ acc, group, resourcesField: 'layerIds' }),
        {},
      );
      const inheritedProjects = selectedGroupPermissions.reduce(
        (acc, group) => reducePermissionsWithGroupName({ acc, group, resourcesField: 'projectIds' }),
        {},
      );

      return {
        inheritedForms,
        inheritedLayers,
        inheritedProjects,
        inheritedFormIds: Object.keys(inheritedForms),
        inheritedLayerIds: Object.keys(inheritedLayers),
        inheritedProjectIds: Object.keys(inheritedProjects),
      };
    },
  };
})();

export default Group;
